import { v4 as uuidv4 } from 'uuid';
import {
  AbstractField,
  FieldJump,
  FieldType,
  LogicJumpActionType,
} from 'queries';
import { useCallback } from 'react';
import { LogicJumpOption } from '../FormBuilder/elements/interface';
import { isSameJumpDest } from '../FormBuilder/lib/findJump';

export const useOnLogicJumpChange = <F extends AbstractField>(
  field: F,
  onChange?: (logicJump: F) => any,
) => {
  const allowMultipleSelection = field.type === FieldType.MULTIPLE_CHOICE;

  const res = useCallback(
    (logicJumpOption: LogicJumpOption, choiceRef: string) => {
      if (!onChange) {
        return;
      }
      onChange({
        ...field,
        jumpActions: updateFieldJumpWithOption(
          field.jumpActions,
          field.id,
          logicJumpOption,
          choiceRef,
          allowMultipleSelection,
        ),
      });
    },
    [field, onChange, allowMultipleSelection],
  );
  return onChange ? res : undefined;
};

const updateFieldJumpWithOption = (
  currentJumps: FieldJump[] = [],
  fieldId: string,
  logicJumpOption: LogicJumpOption,
  choiceRef: string,
  multipleChoiceAllowed = false,
): FieldJump[] => {
  let res: FieldJump[] = [];
  const isDefaultAction =
    logicJumpOption.type === LogicJumpActionType.NEXT_QUESTION;

  let added = false;
  for (const jump of currentJumps) {
    const isSameDest = isSameJumpDest(jump, logicJumpOption);

    if (!isSameDest) {
      if (jump.conditionChoiceRefs?.includes(choiceRef)) {
        if (jump.conditionChoiceRefs.length === 1) {
          continue;
        }
        res.push({
          ...jump,
          conditionChoiceRefs: jump.conditionChoiceRefs.filter(
            (ref) => ref !== choiceRef,
          ),
        });
      } else {
        res.push(jump);
      }
      continue;
    }
    if (isDefaultAction) {
      continue;
    }

    if (!jump.conditionChoiceRefs) {
      res.push(jump);
      continue;
    }

    added = true;
    if (jump.conditionChoiceRefs.includes(choiceRef)) {
      res.push(jump);
      continue;
    }
    res.push({
      ...jump,
      conditionChoiceRefs: [...jump.conditionChoiceRefs, choiceRef],
    });
  }
  if (isDefaultAction) {
    return res;
  }

  if (!added) {
    res.push({
      id: uuidv4(),
      fieldId,
      type: logicJumpOption.type,
      toFieldId: logicJumpOption.toFieldId,
      conditionChoiceRefs: [choiceRef],
    });
  }
  if (multipleChoiceAllowed) {
    res = res.map((logicJumpAction) => {
      if (!logicJumpAction.conditionChoiceRefs) {
        return logicJumpAction;
      }
      return {
        ...logicJumpAction,
        type: logicJumpOption.type,
        toFieldId: logicJumpOption.toFieldId,
      };
    });
  }
  return res;
};
